import { createContext, type ReactNode, useContext, useMemo } from "react";

import { useAuth } from "@/firebase/useAuth";
import { useProgress, type UserProgress } from "@/hooks/useProgress";
import { useUserStats, type UserStats } from "@/hooks/useUserStats";
import { useFirestoreQuery } from "@/hooks/useFirestoreQuery";
import { db } from "@/firebase/firebase-config";
import { doc } from "firebase/firestore";

export type UserProfile = {
  uid?: string;
  email?: string | null;
  displayName?: string | null;
  photoURL?: string | null;
  planTier?: string | null; // Legacy field, use subscriptionTier
  subscriptionTier?: string | null; // Current field in Firestore
  onboardingCompletedAt?: string | null;
  hasOnboarded?: boolean | null;
  tutorialCompletedAt?: string | null;
  completedCourses?: string[];
  unlockedLevels?: string[];
  totalPoints?: number; // Points from the scoring engine
  parentRequired?: boolean;
  parentApproved?: boolean;
  parentEmail?: string | null;
  coachAvatarId?: string | null;
  coachAvatarName?: string | null;
  coachAvatarImage?: string | null;
  coachType?: "coach" | "spirit-animal" | null;
  coachBackgroundColor?: string | null;
  age?: number;
  // Family account fields
  isFamilyAccount?: boolean;
  familyChildCount?: number;
  childAccountIds?: string[];
  // Child account fields
  role?: string | null;
  isChildAccount?: boolean;
  parentAccountId?: string | null;
  accountControlMode?: "parent" | "child" | null;
  familyRole?: "child" | "parent" | null;
};

type UserContextValue = {
  authUser: ReturnType<typeof useAuth>["user"];
  loading: boolean;
  error: string | null;
  profile: (UserProfile & { id?: string }) | null;
  progress: (UserProgress & { id?: string }) | null;
  stats: (UserStats & { id?: string }) | null;
};

const UserContext = createContext<UserContextValue | undefined>(undefined);

export function UserProvider({ children }: { children: ReactNode }) {
  const authState = useAuth();

  const userDoc = useMemo(
    () => (authState.user ? doc(db(), "users", authState.user.uid) : undefined),
    [authState.user]
  );

  const { data: profileData, loading: profileLoading, error: profileError } =
    useFirestoreQuery<UserProfile>(userDoc);
  const { progress, loading: progressLoading, error: progressError } = useProgress(authState.user);
  const { stats, loading: statsLoading, error: statsError } = useUserStats(authState.user);

  const value: UserContextValue = useMemo(
    () => ({
      authUser: authState.user,
      loading: authState.loading || profileLoading || progressLoading || statsLoading,
      error: profileError || progressError || statsError,
      profile: (profileData as UserProfile & { id?: string }) ?? null,
      progress,
      stats,
    }),
    [
      authState.user,
      authState.loading,
      profileData,
      profileLoading,
      profileError,
      progress,
      progressLoading,
      progressError,
      stats,
      statsLoading,
      statsError,
    ]
  );

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
}

export function useUserContext(): UserContextValue {
  const ctx = useContext(UserContext);
  if (!ctx) {
    throw new Error("useUserContext must be used within a UserProvider");
  }
  return ctx;
}
